import './style.css';
import { BaziResult, Wuxing } from './engine/bazi';
import type { Gender } from './engine/calendar';
import {
  compareShichen,
  formatSampleLabel,
  ShichenCompare,
} from './engine/shichen';
import { matchGeju, formatFlagsZh, type GejuMatch, type GejuMatchResult } from './engine/rules';
import {
  buildLunmingPrompt,
  formatGregorianLabel,
  LUNMING_API_STUB,
} from './engine/lunmingPrompt';

const app = document.querySelector<HTMLDivElement>('#app')!;

function pad(n: number): string {
  return String(n).padStart(2, '0');
}

function nowLocalParts() {
  const d = new Date();
  return {
    year: d.getFullYear(),
    month: d.getMonth() + 1,
    day: d.getDate(),
    hour: d.getHours(),
    minute: d.getMinutes(),
  };
}

function toDatetimeLocalValue(y: number, m: number, d: number, h: number, mi: number): string {
  return `${y}-${pad(m)}-${pad(d)}T${pad(h)}:${pad(mi)}`;
}

function parseDatetimeLocal(v: string): { year: number; month: number; day: number; hour: number; minute: number } | null {
  const m = v.match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/);
  if (!m) return null;
  return {
    year: +m[1],
    month: +m[2],
    day: +m[3],
    hour: +m[4],
    minute: +m[5],
  };
}

type ChartKey = 'original' | 'prev' | 'next';

const n0 = nowLocalParts();
let state = {
  ...n0,
  gender: '男' as Gender,
  showLunar: true,
  activeChart: 'original' as ChartKey,
  viewMode: 'tabs' as 'tabs' | 'side',
};

function renderCang(cang: { gan: string; shiShen: string }[]): string {
  if (!cang.length) return '';
  return `<div class="cang-list">${cang
    .map((c) => `<div class="cg"><span class="g">${c.gan}</span><span class="s">${c.shiShen}</span></div>`)
    .join('')}</div>`;
}

function renderPillars(r: BaziResult): string {
  return `<div class="pillars-grid">${r.pillars
    .map(
      (p) => `
    <div class="pillar-card">
      <div class="plabel">${p.label}</div>
      <div class="gz"><span class="gan">${p.gan}</span><span class="zhi">${p.zhi}</span></div>
      <div class="ss">${p.ganShiShen}</div>
      <div>
        <span class="wx-tag">${p.ganWuxing}</span>
        <span class="wx-tag">${p.zhi}·${p.zhiWuxing}</span>
      </div>
      ${renderCang(p.canggan)}
    </div>`,
    )
    .join('')}</div>`;
}

function renderDayun(r: BaziResult): string {
  const d = r.dayun;
  const head = d.items.map((it) => `<th>第${it.index}運</th>`).join('');
  const ages = d.items.map((it) => `<td>${it.startAge}–${it.endAge}歲</td>`).join('');
  const years = d.items.map((it) => `<td>${it.startYear}–${it.endYear}</td>`).join('');
  const gzs = d.items
    .map((it) => `<td class="gz-cell"><span class="gan">${it.gan}</span><span class="zhi">${it.zhi}</span></td>`)
    .join('');
  const ss = d.items.map((it) => `<td class="ss-cell">${it.ganShiShen}</td>`).join('');
  const cg = d.items
    .map(
      (it) =>
        `<td class="ss-cell">${it.canggan.map((c) => `${c.gan}${c.shiShen}`).join(' ')}</td>`,
    )
    .join('');

  return `
    <div class="dayun-meta">
      <div>方向：<strong>${d.ruleLabel}</strong>（${d.forward ? '順行' : '逆行'}）</div>
      <div>起運：約 <strong>${d.qiYunYears}</strong> 年 <strong>${d.qiYunMonths}</strong> 個月
        （距${d.forward ? '下一' : '上一'}節「${d.jieName}」約 ${d.jieDays} 日；三日折一年）</div>
    </div>
    <div class="dayun-scroll">
      <table class="dayun-table">
        <thead><tr><th>項目</th>${head}</tr></thead>
        <tbody>
          <tr><th>年齡</th>${ages}</tr>
          <tr><th>西曆約</th>${years}</tr>
          <tr><th>干支</th>${gzs}</tr>
          <tr><th>天干十神</th>${ss}</tr>
          <tr><th>藏干十神</th>${cg}</tr>
        </tbody>
      </table>
    </div>`;
}

function renderWuxing(r: BaziResult): string {
  const wx = ['木', '火', '土', '金', '水'] as Wuxing[];
  const vis = r.wuxing.visible;
  const max = Math.max(1, ...wx.map((k) => vis[k]));
  const rows = wx
    .map((k) => {
      const n = vis[k];
      const pct = Math.round((n / max) * 100);
      return `<div class="wx-row">
        <span>${k}</span>
        <div class="wx-bar-bg"><div class="wx-bar ${k}" style="width:${pct}%"></div></div>
        <span>${n}</span>
      </div>`;
    })
    .join('');
  const cg = r.wuxing.withCanggan;
  const cgLine = wx.map((k) => `${k}${cg[k]}`).join('　');
  return `<div class="wx-bars">${rows}</div>
    <p class="meta-line" style="margin-top:0.75rem">可見干支五行（天干＋地支本身）。含藏干合計：${cgLine}</p>`;
}

function hourPillar(r: BaziResult): string {
  const h = r.pillars.find((p) => p.label === '時柱')!;
  return h.gz;
}


function statusLabel(m: GejuMatch): string {
  if (m.status === 'ambiguous') return '候選／多格局';
  if (m.status === 'candidate') return '候選 · 待核對';
  return '結構命中 · 待核對';
}

/** Display-only: prefer Chinese; replace internal underscores in tags. */
function displayTag(t: string): string {
  return t.replace(/_/g, '·');
}

function chipList(items: string[], cls: string): string {
  if (!items.length) return '<span class="chip muted">—</span>';
  return items.map((t) => `<span class="chip ${cls}">${displayTag(t)}</span>`).join('');
}

function renderGeju(r: BaziResult): string {
  const res: GejuMatchResult = matchGeju(r);
  const { structural: s, matches, primary } = res;
  const primaryIds = new Set(primary.map((m) => m.rule.id));
  // show primary first, then other matches
  const ordered = [
    ...primary,
    ...matches.filter((m) => !primaryIds.has(m.rule.id)),
  ];
  if (!ordered.length) {
    return `
      <div class="panel compact-panel geju-panel">
        <h3>候補格局＋喜忌標籤</h3>
        <p class="meta-line">未檢出月令格／從旺結構旗（可改 <code>rules/geju.json</code>）。</p>
        <p class="meta-line">月令本氣 <strong>${s.yueLingBenQi}</strong>→${s.yueLingShiShen}；可見主導 ${s.dominant ?? '—'}（${(s.dominantRatio * 100).toFixed(0)}%）</p>
      </div>`;
  }
  const rows = ordered
    .map((m) => {
      const st = statusLabel(m);
      const stClass =
        m.status === 'matched' ? 'st-hit' : m.status === 'ambiguous' ? 'st-amb' : 'st-cand';
      return `<tr>
        <td><strong>${m.rule.name}</strong></td>
        <td><span class="chip ${stClass}">${st}</span></td>
        <td class="chip-cell">${chipList(m.rule.用神_tags, 'tag-yong')}</td>
        <td class="chip-cell">${chipList(m.rule.喜, 'tag-xi')}</td>
        <td class="chip-cell">${chipList(m.rule.忌, 'tag-ji')}</td>
        <td class="reason-cell">${m.reasons.join('；')}</td>
      </tr>`;
    })
    .join('');
  const note = ordered[0]?.rule.source_note ?? '待按《八字提要》核對';
  return `
    <div class="panel compact-panel geju-panel">
      <h3>候補格局＋喜忌標籤</h3>
      <p class="meta-line">
        月令本氣 <strong>${s.yueLingBenQi}</strong>→${s.yueLingShiShen}
        　可見主導 <strong>${s.dominant ?? '—'}</strong>（${(s.dominantRatio * 100).toFixed(0)}%）
        　結構旗 <strong>${formatFlagsZh(s.flags)}</strong>
      </p>
      <div class="geju-scroll">
        <table class="geju-table">
          <thead>
            <tr>
              <th>格局</th><th>狀態</th><th>用神</th><th>喜</th><th>忌</th><th>結構理由</th>
            </tr>
          </thead>
          <tbody>${rows}</tbody>
        </table>
      </div>
      <p class="geju-footnote">來源備註：${note} · 標籤可編輯於 rules/geju.json · 論斷須按書核對</p>
    </div>`;
}

function renderChartBody(r: BaziResult, title: string, rangeLabel: string, sampleLabel: string): string {
  const c = r.calendar;
  const lunar =
    state.showLunar && c.lunarDay != null
      ? `　農曆日：<strong>${c.lunarDay}</strong>`
      : '';
  const notes = c.notes.length
    ? `<div class="notes">${c.notes.map((n) => `· ${n}`).join('<br/>')}</div>`
    : '';

  return `
    <div class="chart-block">
      <div class="chart-head">
        <h2>${title}</h2>
        <div class="range-badge">${rangeLabel}</div>
        <div class="meta-line" style="margin:0.35rem 0 0">
          代表時刻：<strong>${sampleLabel}</strong>
          　日主：<strong>${r.dayMaster}</strong>
          　性別：<strong>${r.gender}</strong>
          　節氣：<strong>${c.jieqi.name}</strong>
          ${lunar}
        </div>
      </div>
      <div class="panel compact-panel">
        <h3>四柱 · 十神</h3>
        ${renderPillars(r)}
        ${notes}
      </div>
      ${renderGeju(r)}
      <div class="panel compact-panel">
        <h3>大運</h3>
        ${renderDayun(r)}
      </div>
      <div class="panel compact-panel">
        <h3>五行（基本計數）</h3>
        ${renderWuxing(r)}
      </div>
    </div>`;
}

function renderCompare(cmp: ShichenCompare): string {
  const { current, prev, next, charts } = cmp;
  const strip = `
    <div class="shichen-strip">
      <div class="sc-card ${state.activeChart === 'prev' ? 'active' : ''}" data-chart="prev">
        <div class="sc-role">前一時辰</div>
        <div class="sc-range">${prev.rangeLabel}</div>
        <div class="sc-gz"><span class="gan">${charts.prev.pillars[3].gan}</span><span class="zhi">${charts.prev.pillars[3].zhi}</span></div>
        <div class="sc-meta">時柱 ${hourPillar(charts.prev)}</div>
      </div>
      <div class="sc-card ${state.activeChart === 'original' ? 'active' : ''}" data-chart="original">
        <div class="sc-role">原局</div>
        <div class="sc-range">${current.rangeLabel}</div>
        <div class="sc-gz"><span class="gan">${charts.original.pillars[3].gan}</span><span class="zhi">${charts.original.pillars[3].zhi}</span></div>
        <div class="sc-meta">時柱 ${hourPillar(charts.original)}</div>
      </div>
      <div class="sc-card ${state.activeChart === 'next' ? 'active' : ''}" data-chart="next">
        <div class="sc-role">後一時辰</div>
        <div class="sc-range">${next.rangeLabel}</div>
        <div class="sc-gz"><span class="gan">${charts.next.pillars[3].gan}</span><span class="zhi">${charts.next.pillars[3].zhi}</span></div>
        <div class="sc-meta">時柱 ${hourPillar(charts.next)}</div>
      </div>
    </div>`;

  const modeToggle = `
    <div class="view-toggle">
      <button type="button" class="view-btn ${state.viewMode === 'tabs' ? 'on' : ''}" data-view="tabs">分頁</button>
      <button type="button" class="view-btn ${state.viewMode === 'side' ? 'on' : ''}" data-view="side">並排</button>
    </div>`;

  if (state.viewMode === 'side') {
    return `
      <div class="panel">
        <h2>前後時辰排盤對比</h2>
        <p class="meta-line">由輸入日時推前後各一個時辰（標準 2 小時塊；子時跨 23:00–01:00）。性別同原局。</p>
        ${modeToggle}
        ${strip}
      </div>
      <div class="side-compare">
        ${renderChartBody(charts.prev, '前一時辰', prev.rangeLabel, formatSampleLabel(prev.sample))}
        ${renderChartBody(charts.original, '原局', current.rangeLabel, formatSampleLabel({
          year: state.year, month: state.month, day: state.day, hour: state.hour, minute: state.minute,
        }))}
        ${renderChartBody(charts.next, '後一時辰', next.rangeLabel, formatSampleLabel(next.sample))}
      </div>`;
  }

  const key = state.activeChart;
  const map = {
    original: {
      r: charts.original,
      title: '原局',
      range: current.rangeLabel,
      sample: formatSampleLabel({
        year: state.year, month: state.month, day: state.day, hour: state.hour, minute: state.minute,
      }),
    },
    prev: {
      r: charts.prev,
      title: '前一時辰',
      range: prev.rangeLabel,
      sample: formatSampleLabel(prev.sample),
    },
    next: {
      r: charts.next,
      title: '後一時辰',
      range: next.rangeLabel,
      sample: formatSampleLabel(next.sample),
    },
  } as const;
  const cur = map[key];

  return `
    <div class="panel">
      <h2>前後時辰排盤對比</h2>
      <p class="meta-line">由輸入日時推前後各一個時辰（標準 2 小時塊；子時跨 23:00–01:00）。性別同原局。撳下面卡片切換。</p>
      ${modeToggle}
      ${strip}
    </div>
    ${renderChartBody(cur.r, cur.title, cur.range, cur.sample)}`;
}

let lastPromptText = '';

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function showToast(msg: string): void {
  let el = document.getElementById('toast');
  if (!el) {
    el = document.createElement('div');
    el.id = 'toast';
    el.className = 'toast';
    document.body.appendChild(el);
  }
  el.textContent = msg;
  el.classList.add('show');
  window.clearTimeout((el as HTMLElement & { _t?: number })._t);
  (el as HTMLElement & { _t?: number })._t = window.setTimeout(() => {
    el!.classList.remove('show');
  }, 1600);
}

async function copyText(text: string): Promise<boolean> {
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch {
    /* fall through */
  }
  try {
    const ta = document.createElement('textarea');
    ta.value = text;
    ta.setAttribute('readonly', '');
    ta.style.position = 'fixed';
    ta.style.left = '-9999px';
    document.body.appendChild(ta);
    ta.select();
    const ok = document.execCommand('copy');
    document.body.removeChild(ta);
    return ok;
  } catch {
    return false;
  }
}

function renderLunmingPanel(cmp: ShichenCompare): string {
  const gregorianLabel = formatGregorianLabel(
    state.year,
    state.month,
    state.day,
    state.hour,
    state.minute,
  );
  lastPromptText = buildLunmingPrompt({
    gregorianLabel,
    gender: state.gender,
    cmp,
  });
  const preview = escapeHtml(lastPromptText);
  const stubNote = LUNMING_API_STUB.note;
  return `
    <div class="panel lunming-panel" id="lunming-panel">
      <h2>論命提示詞</h2>
      <p class="meta-line">排盤成功後可複製下方完整提示詞，貼去外部 AI（ChatGPT／Claude／Grok 等）論命。本站<strong>唔內嵌</strong>《八字提要》全文；提示詞要求外部 AI 跟韋千里《八字提要》框架，並禁止農場文／紫微。</p>
      <div class="btn-row lunming-actions">
        <button type="button" class="primary" id="btn-copy-prompt">複製論命提示詞</button>
        <button type="button" class="ghost" id="btn-lunming-api" disabled title="${escapeHtml(stubNote)}">論命（API 未接）</button>
      </div>
      <p class="api-stub-note">${escapeHtml(stubNote)}</p>
      <details class="prompt-preview" id="prompt-preview">
        <summary>預覽提示詞全文</summary>
        <pre class="prompt-pre">${preview}</pre>
      </details>
    </div>`;
}

function computeAndPaint(): void {
  const input = {
    year: state.year,
    month: state.month,
    day: state.day,
    hour: state.hour,
    minute: state.minute,
    gender: state.gender,
  };
  const cmp = compareShichen(input);
  const out = document.getElementById('result');
  if (out) {
    out.innerHTML = renderLunmingPanel(cmp) + renderCompare(cmp);
    bindLunmingHandlers();
    bindCompareHandlers();
  }
}

function bindLunmingHandlers(): void {
  const btn = document.getElementById('btn-copy-prompt');
  if (btn) {
    btn.addEventListener('click', async () => {
      const ok = await copyText(lastPromptText);
      showToast(ok ? '已複製' : '複製失敗');
    });
  }
}

function bindCompareHandlers(): void {
  document.querySelectorAll<HTMLElement>('.sc-card[data-chart]').forEach((el) => {
    el.addEventListener('click', () => {
      state.activeChart = el.dataset.chart as ChartKey;
      state.viewMode = 'tabs';
      computeAndPaint();
    });
  });
  document.querySelectorAll<HTMLElement>('.view-btn[data-view]').forEach((el) => {
    el.addEventListener('click', () => {
      state.viewMode = el.dataset.view as 'tabs' | 'side';
      computeAndPaint();
    });
  });
}

function paintShell(): void {
  const dtVal = toDatetimeLocalValue(state.year, state.month, state.day, state.hour, state.minute);
  app.innerHTML = `
    <h1>Topwin 八字排盤</h1>
    <p class="subtitle">子平四柱 · 十神 · 大運 · 候補格局／喜忌標籤 · 前後時辰對比 · 複製論命提示詞（論斷待核對）</p>
    <div class="panel">
      <div class="controls">
        <label>公曆日時
          <input type="datetime-local" id="dt" value="${dtVal}" />
        </label>
        <label>性別
          <select id="gender">
            <option value="男" ${state.gender === '男' ? 'selected' : ''}>男</option>
            <option value="女" ${state.gender === '女' ? 'selected' : ''}>女</option>
          </select>
        </label>
        <label style="flex-direction:row;align-items:center;gap:0.4rem;margin-top:1.1rem">
          <input type="checkbox" id="lunar" ${state.showLunar ? 'checked' : ''} />
          顯示農曆日
        </label>
        <div class="btn-row" style="margin-top:0.2rem">
          <button type="button" class="primary" id="btn-go">排盤</button>
          <button type="button" id="btn-now">而家</button>
        </div>
      </div>
    </div>
    <div id="result"></div>
    <p class="footer-note">曆法預表 1900–2100 · 公式見 ENGINE.md · 唔會覆寫奇門專案</p>
  `;

  document.getElementById('btn-go')!.addEventListener('click', () => {
    const raw = (document.getElementById('dt') as HTMLInputElement).value;
    const parsed = parseDatetimeLocal(raw);
    if (!parsed) return;
    Object.assign(state, parsed);
    state.gender = (document.getElementById('gender') as HTMLSelectElement).value as Gender;
    state.showLunar = (document.getElementById('lunar') as HTMLInputElement).checked;
    state.activeChart = 'original';
    computeAndPaint();
  });

  document.getElementById('btn-now')!.addEventListener('click', () => {
    Object.assign(state, nowLocalParts());
    (document.getElementById('dt') as HTMLInputElement).value = toDatetimeLocalValue(
      state.year,
      state.month,
      state.day,
      state.hour,
      state.minute,
    );
    state.activeChart = 'original';
    computeAndPaint();
  });

  document.getElementById('gender')!.addEventListener('change', (e) => {
    state.gender = (e.target as HTMLSelectElement).value as Gender;
    computeAndPaint();
  });

  document.getElementById('lunar')!.addEventListener('change', (e) => {
    state.showLunar = (e.target as HTMLInputElement).checked;
    computeAndPaint();
  });

  document.getElementById('dt')!.addEventListener('change', () => {
    const raw = (document.getElementById('dt') as HTMLInputElement).value;
    const parsed = parseDatetimeLocal(raw);
    if (parsed) Object.assign(state, parsed);
  });

  computeAndPaint();
}

paintShell();
